/**
 * 从 discount map 中解析某个模型的折扣系数。
 * 优先精确匹配 modelId → 通配符 "*" → 返回 1（不打折）。
 * 返回值 ∈ [0, 1]，0 = 免费，1 = 原价。
 */
export function resolveDiscountMultiplier(
  discountMap: Record<string, unknown> | null | undefined,
  modelId: string,
): number {
  if (!discountMap || typeof discountMap !== 'object') return 1;
  const raw = (discountMap as any)[modelId] ?? (discountMap as any)['*'];
  const n = Number(raw);
  if (!Number.isFinite(n)) return 1;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * 解析后台/表单输入的折扣值，归一化为合法系数 ∈ [0,1]。
 *
 * 空字符串 / null / undefined / 非数字一律回退到 1（原价）。这一点很关键：
 * `Number('')` 等于 `0` 且通过 `Number.isFinite`，若不先判空会把「清空字段」
 * 误存成 0（免费）。负值裁剪为 0，大于 1 裁剪为 1。
 */
export function parseDiscountInput(raw: unknown): number {
  const str = String(raw ?? '').trim();
  if (str === '') return 1;
  const n = Number(str);
  if (!Number.isFinite(n)) return 1;
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/**
 * 把模型卡折扣系数应用到次卡消耗数量上，round 到 2 位小数（与 model_pass.remaining
 * 的 numeric(12,2) 精度对齐）。
 *
 * 服务端实扣（applyPassDiscountToCountForBillingOwner）与客户端预估
 * （useEstimatedBilling）共用本函数，保证「界面预估 = 实际扣卡」口径一致。
 * 系数 >= 1 时原样返回（不打折）；<= 0 时返回 0（免费）。
 */
export function applyPassDiscountToCount(count: number, multiplier: number): number {
  // 注意 Math.max(0, NaN) === NaN：必须先 isFinite 归零，否则 NaN 会传导成 NaN 扣卡数。
  const n = Number(count ?? 0);
  const base = Number.isFinite(n) ? Math.max(0, n) : 0;
  if (base <= 0) return 0;
  const m = Number(multiplier);
  if (!Number.isFinite(m) || m >= 1) return base;
  if (m <= 0) return 0;
  return Math.round(base * m * 100) / 100;
}

/**
 * 折扣来源：用于在 UI 上标明这笔折扣是「全体折扣 / 专属折扣 / 套餐折扣 / 团队折扣」。
 *   global = 全体（model_access.discount，对所有人生效）
 *   user   = 个人专属（user_discount）
 *   plan   = 套餐（plan_discount）
 *   org    = 团队（org_discount）
 */
export type DiscountSource = 'global' | 'user' | 'plan' | 'org';

/**
 * 从 source map 中解析某模型生效折扣的来源，查找规则与
 * {@link resolveDiscountMultiplier} 一致：精确 modelId → 通配符 "*" → null。
 */
export function resolveDiscountSource(
  sourceMap: Record<string, DiscountSource> | null | undefined,
  modelId: string,
): DiscountSource | null {
  if (!sourceMap || typeof sourceMap !== 'object') return null;
  return sourceMap[modelId] ?? sourceMap['*'] ?? null;
}

/**
 * 合并多个带来源标签的 discount map，每个 key 取更优惠（更小）值，并记录达到该
 * 最小值的来源。layers 顺序为「优先级从低到高」：系数相等时，靠后的来源覆盖
 * （即更具体/更专属的来源胜出）。返回的 discountMap 与旧逻辑等价，可直接喂给
 * {@link resolveDiscountMultiplier} 做成本计算；sourceMap 仅供 UI 标注。
 */
export function mergeDiscountMapsWithSources(
  layers: { source: DiscountSource; map: Record<string, unknown> | null | undefined }[],
): {
  discountMap: Record<string, number> | null;
  sourceMap: Record<string, DiscountSource> | null;
} {
  const valid = layers.filter(
    (l): l is { source: DiscountSource; map: Record<string, unknown> } =>
      !!l.map && typeof l.map === 'object',
  );
  if (valid.length === 0) return { discountMap: null, sourceMap: null };

  const valueAt = (map: Record<string, unknown>, key: string): number => {
    if (key in map) return Number((map as any)[key]);
    if ('*' in map) return Number((map as any)['*']);
    return NaN;
  };

  const keys = new Set<string>();
  for (const { map } of valid) for (const k of Object.keys(map)) keys.add(k);

  const discountMap: Record<string, number> = {};
  const sourceMap: Record<string, DiscountSource> = {};
  for (const k of keys) {
    let best = Number.POSITIVE_INFINITY;
    let bestSource: DiscountSource | null = null;
    for (const { source, map } of valid) {
      const v = valueAt(map, k);
      // <= 让靠后（更专属）的来源在并列时胜出
      if (Number.isFinite(v) && v <= best) {
        best = v;
        bestSource = source;
      }
    }
    if (bestSource !== null && Number.isFinite(best)) {
      discountMap[k] = best;
      sourceMap[k] = bestSource;
    }
  }

  return {
    discountMap: Object.keys(discountMap).length > 0 ? discountMap : null,
    sourceMap: Object.keys(sourceMap).length > 0 ? sourceMap : null,
  };
}
